<template>
    <div class="form-container">
        <div class="form-section">
            <div class="form-group">
                <label>Action to Run</label>
                <select v-model="selectedAction">
                    <option value="">Select Action</option>
                    <option v-for="action in actions" :key="action.name" :value="action">
                        {{ action.name }} ({{ action.type }})
                    </option>
                </select>
            </div>

            <div v-if="selectedAction" class="form-group" style="padding: 10px; background-color: var(--color-ds-light-background); border-radius: var(--radius-sm);">
                <label style="font-weight: bold;">Action Details</label>
                <div v-for="(value, key) in selectedAction.params" :key="key" style="margin: 5px 0; font-size: var(--text-sm);">
                    <strong>{{ key }}:</strong> {{ value }}
                </div>
            </div>

            <button class="button button-full" @click="runAction" :disabled="!selectedAction || running">
                {{ running ? 'Running...' : 'Run Action' }}
            </button>
        </div>

        <div v-if="statusMessage" :class="isError ? 'status-error' : 'status-success'">
            {{ statusMessage }}
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { showMessage } from '../utils/messages';
import { uploadTableData } from '../services/importDataService';
import { executeWorkflowAction } from '../services/workflowExecutionService';

const actions = ref([]);
const selectedAction = ref(null);
const running = ref(false);
const statusMessage = ref('');
const isError = ref(false);

// Read workflow actions from the Workflow sheet
const loadWorkflowActions = async () => {
    try {
        await window.Excel.run(async (context) => {
            const workbook = context.workbook;
            const workflowSheet = workbook.worksheets.getItemOrNullObject('Workflow');
            workflowSheet.load('name');
            await context.sync();

            if (workflowSheet.isNullObject) {
                showMessage('Workflow sheet not found', true);
                return;
            }

            const usedRange = workflowSheet.getUsedRange();
            usedRange.load('values, columnCount');
            await context.sync();

            const values = usedRange.values;
            const columnCount = usedRange.columnCount;

            // Parse workflow items (each action is in a column pair: keys and values)
            const parsedActions = [];

            for (let col = 0; col < columnCount; col += 2) {
                const keys = [];
                const vals = [];

                // Read keys column
                for (let row = 0; row < values.length; row++) {
                    const key = values[row][col];
                    if (key) {
                        keys.push(key);
                    } else {
                        break; // Stop when we hit an empty cell
                    }
                }

                // Read values column
                if (col + 1 < columnCount) {
                    for (let row = 0; row < keys.length; row++) {
                        vals.push(values[row][col + 1]);
                    }
                }

                // Create action object
                if (keys.length > 0 && vals.length > 0) {
                    const params = {};
                    for (let i = 0; i < keys.length; i++) {
                        params[keys[i]] = vals[i];
                    }

                    if (params.ActionName && params.ActionType) {
                        parsedActions.push({
                            name: params.ActionName,
                            type: params.ActionType,
                            params: params
                        });
                    }
                }
            }

            actions.value = parsedActions;
        });
    } catch (error) {
        console.error('Error loading workflow actions:', error);
        showMessage('Error loading workflow actions: ' + error.message, true);
    }
};

// Execute the selected action
const runAction = async () => {
    if (!selectedAction.value) {
        return;
    }

    running.value = true;
    statusMessage.value = '';
    isError.value = false;

    try {
        const action = selectedAction.value;
        const actionType = action.type;

        showMessage(`Running ${action.name} (${actionType})...`);

        if (actionType === 'ImportData') {
            await runImportDataAction(action);
        } else if (actionType === 'ExportData') {
            await runExportDataAction(action);
        } else {
            statusMessage.value = `⚠️ Action type "${actionType}" not yet implemented`;
            isError.value = false;
        }

    } catch (error) {
        console.error('Error running action:', error);
        statusMessage.value = `❌ Error: ${error.message}`;
        isError.value = true;
        showMessage(statusMessage.value, true);
    } finally {
        running.value = false;
    }
};

// Run ImportData action
const runImportDataAction = async (action) => {
    const params = action.params;

    // Validate required parameters
    if (!params.SheetName) {
        throw new Error('SheetName parameter is missing');
    }
    if (!params.ModelName) {
        throw new Error('ModelName parameter is missing');
    }
    if (!params.CosmicFrogTable) {
        throw new Error('CosmicFrogTable parameter is missing');
    }

    const upsertOrReplace = params.UpsertOrReplace || 'Upsert';

    // Execute the import
    const result = await uploadTableData(
        params.ModelName,
        params.CosmicFrogTable,
        params.SheetName,
        upsertOrReplace
    );

    statusMessage.value = `✅ Successfully imported ${result.rowCount} rows from "${params.SheetName}" to table "${params.CosmicFrogTable}" (${upsertOrReplace} mode)`;
    isError.value = false;
    showMessage(statusMessage.value);
};

// Run ExportData action
const runExportDataAction = async (action) => {
    const params = action.params;

    // Validate required parameters
    if (!params.CosmicFrogTable) {
        throw new Error('CosmicFrogTable parameter is missing');
    }
    if (!params.SheetName) {
        throw new Error('SheetName parameter is missing');
    }

    // Update status with progress messages
    const updateStatus = (message) => {
        statusMessage.value = message;
        showMessage(message);
    };

    // Execute the export using workflow execution service
    const result = await executeWorkflowAction(action, updateStatus);

    statusMessage.value = `✅ ${result.message}`;
    isError.value = false;
    showMessage(statusMessage.value);
};

onMounted(async () => {
    await loadWorkflowActions();
});
</script>
